/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
  FormControlLabel,
  Switch,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
} from "@mui/icons-material";
import { createNewRole } from "store/features/admin/roleManagement/role/roleActions";
import addRoleSvg from "assets/modals/add.svg";

const AddRoleModal = ({ isOpen, onClose, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    is_system: 0,
    priority: "",
    status: "active",
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Role name is required";
    if (!formData.code.trim()) newErrors.code = "Role code is required";
    if (!formData.code.trim().match(/^[A-Z_]+$/)) {
      newErrors.code =
        "Role code must be uppercase letters and underscores only";
    }
    if (
      formData.priority &&
      (formData.priority < 0 || formData.priority > 10)
    ) {
      newErrors.priority = "Priority must be between 0 and 10";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const formattedData = {
      ...formData,
      priority: formData.priority ? Number(formData.priority) : 0,
      is_system: Number(formData.is_system),
    };

    const result = await dispatch(createNewRole(formattedData));

    if (result.success) {
      onSuccess();
      onClose();
      resetForm();
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      description: "",
      is_system: 0,
      priority: "",
      status: "active",
    });
    setErrors({});
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#f59e0b",
    },
  };

  const statusOptions = [
    { value: "active", label: "Active", color: "#10b981" },
    { value: "inactive", label: "Inactive", color: "#ef4444" },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>
            {`
          .MuiPopover-root {
            z-index: 1400 !important;
          }
          .MuiPopper-root {
            z-index: 1400 !important;
          }
        `}
          </style>
          {/* Backdrop with fade animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(59, 130, 246, 0.1)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          {/* Modal with slide-in from right animation */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "600px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={addRoleSvg}
                      alt="Add Role"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(59,130,246,0.3))",
                        },
                      }}
                      onError={(e) => {
                        // Fallback icon if SVG doesn't exist
                        e.target.style.display = "none";
                        const parent = e.target.parentElement;
                        if (parent && !parent.querySelector(".fallback-icon")) {
                          const fallbackIcon = document.createElement("div");
                          fallbackIcon.className = "fallback-icon";
                          fallbackIcon.innerHTML =
                            '<svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 4c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm0 13c-2.33 0-4.31-1.46-5.11-3.5h10.22c-.8 2.04-2.78 3.5-5.11 3.5z" fill="#3b82f6"/></svg>';
                          fallbackIcon.style.width = "80px";
                          fallbackIcon.style.height = "80px";
                          parent.appendChild(fallbackIcon);
                        }
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                      }}
                    >
                      Add New Role
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Create a new role with specific permissions and access
                      levels
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {/* Info Alert */}
                  <Alert
                    severity="info"
                    sx={{
                      borderRadius: "12px",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(246, 174, 59, 0.1)"
                          : "#fffbef",
                      "& .MuiAlert-icon": {
                        color: "#f59e0b",
                      },
                    }}
                  >
                    <Typography variant="body2">
                      Role codes should be in uppercase with underscores (e.g.,
                      ADMIN, HR_MANAGER)
                    </Typography>
                  </Alert>

                  {/* Name and Code Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Role Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="e.g., HR Manager, Team Lead"
                      helperText="Display name for the role"
                      error={!!errors.name}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Role Code"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      required
                      placeholder="e.g., HR_MANAGER, TEAM_LEAD"
                      helperText={
                        errors.code || "Unique identifier for the role"
                      }
                      error={!!errors.code}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  {/* Description */}
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Describe the responsibilities and permissions for this role..."
                    helperText="Brief description of what this role can do"
                    sx={inputSx}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  {/* Priority and Status Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Priority"
                      name="priority"
                      type="number"
                      value={formData.priority}
                      onChange={handleChange}
                      onInput={(e) => {
                        const value = parseInt(e.target.value);
                        if (value > 10) {
                          e.target.value = 10;
                          setFormData((prev) => ({ ...prev, priority: 10 }));
                        }
                        if (value < 0) {
                          e.target.value = 0;
                          setFormData((prev) => ({ ...prev, priority: 0 }));
                        }
                      }}
                      placeholder="0"
                      helperText={
                        errors.priority ||
                        "Lower number = higher priority (0-10)"
                      }
                      error={!!errors.priority}
                      InputProps={{ inputProps: { min: 0, max: 10, step: 1 } }}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Status"
                      name="status"
                      select
                      value={formData.status}
                      onChange={handleChange}
                      required
                      helperText="Active roles can be assigned to users"
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: option.color,
                              }}
                            />
                            <Typography>{option.label}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  {/* System Role Switch */}
                  <FormControlLabel
                    control={
                      <Switch
                        name="is_system"
                        checked={formData.is_system === 1}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_system: e.target.checked ? 1 : 0,
                          }))
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#f59e0b",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#f59e0b",
                            },
                        }}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AdminIcon sx={{ fontSize: 20, color: "#f59e0b" }} />
                        <Typography
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#e5e7eb"
                                : "#374151",
                          }}
                        >
                          System Role
                        </Typography>
                      </Stack>
                    }
                  />

                  {/* Note for system roles */}
                  {formData.is_system === 1 && (
                    <Alert
                      severity="warning"
                      sx={{
                        borderRadius: "12px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(245,158,11,0.1)"
                            : "#fef3c7",
                      }}
                    >
                      <Typography variant="body2">
                        System roles have elevated permissions and cannot be
                        deleted. Make sure this is intentional.
                      </Typography>
                    </Alert>
                  )}
                </Stack>
              </Box>

              {/* Footer Buttons */}
              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  variant="contained"
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Create Salary Structure"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddRoleModal;
